import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {CLOSE_EVENT, FOCUS_REQUEST_EVENT} from '@app/services/iframe/iframe-message.service';
import {SupportFormComponent} from '../iframe/support-form.component';
import {DcpSupportFormHost, DcpSupportFormService} from '../dcp-support-form.service';

/**
 * Host for the support form. Mounted once globally in {@link AppComponent}.
 *
 * Owns a {@code <div popover="manual">} that wraps {@code <dcp-support-form>}.
 * When the user opens the support form (header link, Contact Us widget, error
 * toasts, configurator edit-cart flow, …), {@link DcpSupportFormService.open}
 * calls into this component, which calls {@code showPopover()} to promote the
 * popover — and its iframe child — into the browser top layer (fixes DCP-57010:
 * a {@code udex-modal} can no longer cover the support form).
 *
 * Keyboard focus trap: once the remote app signals readiness via
 * {@link FOCUS_REQUEST_EVENT}, the host focuses the cross-origin {@code <iframe>}.
 * A focused cross-origin iframe already acts as a natural Tab boundary — Tab
 * keystrokes stay within the iframe's own document until the remote app moves
 * focus out. No additional CDK focus trap is needed, and adding one would break
 * clicks inside the iframe (DCP-61234).
 *
 * The form is rendered eagerly so the inner {@code <iframe>} loads exactly
 * once on app start, matching the previous Spartacus-{@link LaunchDialogService}
 * behavior where the dialog component was kept around across opens.
 */
@Component({
  selector: 'dcp-support-form-host',
  templateUrl: './dcp-support-form-host.component.html',
  styleUrls: ['./dcp-support-form-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SupportFormComponent],
})
export class DcpSupportFormHostComponent implements OnInit, AfterViewInit, DcpSupportFormHost {
  @ViewChild('popover', {static: true}) popoverRef!: ElementRef<HTMLDivElement>;

  private isOpen = false;

  /**
   * Reacts to postMessages from the embedded iframe.
   *
   * - {@link CLOSE_EVENT}: the remote {@code dcp-support-ui} app asks its parent
   *   to close the popover.
   * - {@link FOCUS_REQUEST_EVENT}: the remote app has mounted its form and asks
   *   the host to move keyboard focus onto the {@code <iframe>} element. The
   *   browser only routes Tab keystrokes into a cross-origin iframe when the
   *   host has actually focused the {@code <iframe>} — and a cross-origin iframe
   *   can't focus itself, only the host can.
   *
   * Bound at the window level because the message originates inside the iframe,
   * not from our own DOM.
   */
  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (event?.data?.eventId === CLOSE_EVENT) {
      this.hide();
    } else if (event?.data?.eventId === FOCUS_REQUEST_EVENT) {
      this.focusIframe();
    }
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private dcpSupportFormService: DcpSupportFormService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.dcpSupportFormService.registerHost(this);
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  show(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const el = this.popoverRef?.nativeElement;
    if (!el) {
      return;
    }
    if (!this.isOpen) {
      el.showPopover();
      this.isOpen = true;
      this.cdr.markForCheck();
    }
  }

  hide(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const el = this.popoverRef?.nativeElement;
    if (!el) {
      return;
    }
    if (this.isOpen) {
      el.hidePopover();
      this.isOpen = false;
      this.cdr.markForCheck();
    }
  }

  private focusIframe(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.isOpen) {
      return;
    }
    this.popoverRef?.nativeElement.querySelector<HTMLIFrameElement>('iframe')?.focus();
  }
}
